import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  createCourseTag,
  deleteCourseTag,
  listCourseTags,
  updateCourseTag,
  type CourseTag,
} from '../lib/creators-api';

/**
 * Course tags — the many-to-many labels a course carries BESIDE its single
 * topic. A course has exactly one topic (Course Topics) but any number of
 * tags, so this list is deliberately FLAT: nesting belongs to topics, where a
 * parent means something for the catalog's chips. Hiding a tag keeps it on its
 * courses but drops it from public surfaces; deleting one just un-labels them.
 */
export function CourseTagsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-course-tags'], queryFn: listCourseTags });
  const [name, setName] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-course-tags'] });

  const create = useMutation({
    mutationFn: createCourseTag,
    meta: { successMessage: 'Tag created.' },
    onSuccess: () => {
      invalidate();
      setName('');
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CourseTag> }) =>
      updateCourseTag(id, patch),
    meta: { successMessage: 'Tag updated.' },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: deleteCourseTag,
    meta: { successMessage: 'Tag deleted.' },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['admin-courses'] });
    },
  });
  const tags = q.data ?? [];

  const move = (i: number, dir: -1 | 1) => {
    const a = tags[i];
    const b = tags[i + dir];
    if (!a || !b) return;
    // Swap sort orders; when two rows share one, nudge so the move sticks.
    const aOrder = a.sortOrder === b.sortOrder ? b.sortOrder + (dir === -1 ? -1 : 1) : b.sortOrder;
    update.mutate({ id: a.id, patch: { sortOrder: Math.max(0, aOrder) } });
    if (a.sortOrder !== b.sortOrder) update.mutate({ id: b.id, patch: { sortOrder: a.sortOrder } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Course tags</h1>
        <p className="text-sm text-muted-foreground mt-1">
          The extra labels a course can carry beside its single topic — a course sits in one topic
          but can wear as many tags as fit. Use them for the cross-cutting words a topic list would
          never hold cleanly (&quot;beginner&quot;, &quot;live sessions&quot;,
          &quot;certificate&quot;).
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Tags</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate({ name: name.trim(), sortOrder: tags.length });
            }}
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="New tag (e.g. Beginner, Certificate, Live)"
              className="max-w-sm"
            />
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              + Add
            </Button>
          </form>
          {q.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : tags.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tags yet. Courses without tags list normally — tags only add ways to find them.
            </p>
          ) : (
            <ul className="divide-y">
              {tags.map((t, i) => (
                <TagRow
                  key={t.id}
                  tag={t}
                  first={i === 0}
                  last={i === tags.length - 1}
                  onMove={(dir) => move(i, dir)}
                  onSave={(patch) => update.mutate({ id: t.id, patch })}
                  onToggleActive={() => update.mutate({ id: t.id, patch: { active: !t.active } })}
                  onDelete={() => {
                    if (
                      confirm(
                        `Delete tag "${t.name}"? Courses wearing it simply lose the label — nothing is unpublished.`,
                      )
                    ) {
                      remove.mutate(t.id);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TagRow({
  tag,
  first,
  last,
  onMove,
  onSave,
  onToggleActive,
  onDelete,
}: {
  tag: CourseTag;
  first: boolean;
  last: boolean;
  onMove: (dir: -1 | 1) => void;
  onSave: (patch: Partial<CourseTag>) => void;
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(tag.name);

  if (editing) {
    return (
      <li className="py-2">
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim() && value.trim() !== tag.name) onSave({ name: value.trim() });
            setEditing(false);
          }}
        >
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-8 w-56"
            autoFocus
          />
          <Button type="submit" size="sm">
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setValue(tag.name);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-2 py-2">
      <div className="flex flex-col">
        <button
          type="button"
          className="text-xs leading-none text-muted-foreground disabled:opacity-30"
          disabled={first}
          onClick={() => onMove(-1)}
          aria-label="Move up"
        >
          ▲
        </button>
        <button
          type="button"
          className="text-xs leading-none text-muted-foreground disabled:opacity-30"
          disabled={last}
          onClick={() => onMove(1)}
          aria-label="Move down"
        >
          ▼
        </button>
      </div>
      <span className="text-sm font-medium">{tag.name}</span>
      {!tag.active && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Hidden</span>
      )}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={onToggleActive}>
          {tag.active ? 'Hide' : 'Show'}
        </Button>
        <Button variant="outline" size="sm" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </li>
  );
}
